import { createSlice, PayloadAction } from "@reduxjs/toolkit"

import { NETWORK_DOWNLOAD_SPEED, NETWORK_UPLOAD_SPEED } from "lib/constants"
import { SystemCheckData, SystemCheckSection, SystemCheckStatus, SystemCheckStep } from "types/systemCheck"
import { INTERVIEW_MODE } from "utils/interview"
import { isTabletAndBelow } from "utils/responsive"
import { SystemCheckSectionIds } from "utils/systemChecks"

const initialState: SystemCheckData = {
  sections: [
    {
      id: SystemCheckSectionIds.system_compatability,
      selfSectionIndex: 0,
      title: "System Compatibility Check",
      stepIndex: 0,
      status: "pending",
      requiresProctoring: false,
      checks: [
        {
          title: "Browser & OS Check",
          description: "Checking your browser settings to check if your browser is compatible for the test...",
          status: "pending",
          checkComponentName: "BrowserCheck",
          selfSectionIndex: 0,
          selfStepIndex: 0,
        },
        {
          title: "Network Check",
          description: `Your network connection should be stable and fast. We recommend a minimum of download speed of ${NETWORK_DOWNLOAD_SPEED} Mbps and Upload speed of ${NETWORK_UPLOAD_SPEED} Mbps.`,
          status: "pending",
          checkComponentName: "NetworkCheck",
          selfSectionIndex: 0,
          selfStepIndex: 1,
        },
        {
          title: "Continue",
          description: "Click the button to continue.",
          status: "pending",
          checkComponentName: "PauseCheck",
          selfSectionIndex: 0,
          selfStepIndex: 2,
          full: true,
          hidden: {
            before: true,
            after: true,
          },
          resetOnBack: true,
        },
      ],
    },
    {
      id: SystemCheckSectionIds.io_devices,
      selfSectionIndex: 1,
      title: "I/O Devices Check",
      stepIndex: 0,
      status: "pending",
      requiresProctoring: false,
      checks: [
        {
          title: "I/O Devices Setup",
          description: "Make sure your camera is connected and working properly.",
          status: "pending",
          checkComponentName: "SetupCheck",
          selfSectionIndex: 1,
          selfStepIndex: 0,
          full: true,
        },
      ],
    },
    {
      id: SystemCheckSectionIds.mobile_camera_pairing,
      selfSectionIndex: 2,
      title: "Mobile Camera Pairing",
      stepIndex: 0,
      status: "pending",
      requiresProctoring: true,
      checks: [
        {
          title: "Mobile Camera Pairing",
          description: "Make sure your camera is connected and working properly.",
          status: "pending",
          checkComponentName: "MobileCameraCheck",
          selfSectionIndex: 1,
          selfStepIndex: 0,
          full: true,
        },
      ],
    },
    {
      id: SystemCheckSectionIds.screen_share_permission,
      selfSectionIndex: 3,
      title: "Screen Share Permission",
      stepIndex: 0,
      status: "pending",
      requiresProctoring: true,
      checks: [
        {
          title: "Screen Share Permission",
          description: "We need your permission to share your screen.",
          status: "pending",
          checkComponentName: "ScreenshareCheck",
          selfSectionIndex: 2,
          selfStepIndex: 0,
          full: true,
        },
      ],
    },
    {
      id: SystemCheckSectionIds.full_screen_mode,
      selfSectionIndex: 3,
      title: "Full Screen Mode",
      stepIndex: 0,
      status: "pending",
      requiresProctoring: true,
      checks: [
        {
          title: "Full Screen Mode",
          description: "Make sure you are in full-screen mode.",
          status: "pending",
          checkComponentName: "FullscreenCheck",
          selfSectionIndex: 3,
          selfStepIndex: 0,
          full: true,
        },
      ],
    },
  ],
  sectionIndex: 0,
  isAutomatedFlowModalOpen: false,
  automatedFlow: false,
  data: {},
}

const systemCheckSlice = createSlice({
  name: "systemCheck",
  initialState,
  reducers: {
    setSystemCheckData: (state, action: PayloadAction<SystemCheckData>) => {
      state.sections = action.payload.sections
      state.sectionIndex = action.payload.sectionIndex
      state.data = action.payload.data
    },
    setSystemCheckStepIndex: (
      state,
      action: PayloadAction<{
        sectionIndex: number
        stepIndex: number
      }>
    ) => {
      let activeSectionIndex: number = state.sectionIndex
      if (activeSectionIndex === -1) activeSectionIndex = Infinity

      const section: SystemCheckSection = state.sections[action.payload.sectionIndex] || {
        id: SystemCheckSectionIds.system_compatability,
        selfSectionIndex: -1,
        title: "",
        stepIndex: 0,
        checks: [],
        status: "pending",
        requiresProctoring: true,
      }

      if (section.selfSectionIndex === -1) return
      section.stepIndex = action.payload.stepIndex

      if (activeSectionIndex < section.selfSectionIndex) return
      if (activeSectionIndex > section.selfSectionIndex) {
        state.sectionIndex = section.selfSectionIndex
        activeSectionIndex = section.selfSectionIndex
      }

      let activeSection: SystemCheckSection = state.sections[state.sectionIndex] || {
        id: SystemCheckSectionIds.system_compatability,
        selfSectionIndex: -1,
        title: "",
        stepIndex: -1,
        checks: [],
        status: "pending",
        requiresProctoring: true,
      }

      while (activeSection.stepIndex === activeSection.checks.length) {
        state.sectionIndex = state.sectionIndex + 1
        activeSection = state.sections[state.sectionIndex] || {
          id: SystemCheckSectionIds.system_compatability,
          selfSectionIndex: -1,
          title: "",
          stepIndex: -1,
          checks: [],
          status: "pending",
          requiresProctoring: true,
        }
      }
    },
    setSystemCheckStepStatus: (
      state,
      action: PayloadAction<{
        sectionIndex: number
        stepIndex: number
        status: SystemCheckStatus
      }>
    ) => {
      let section: SystemCheckSection = state.sections[action.payload.sectionIndex] || {
        id: SystemCheckSectionIds.system_compatability,
        selfSectionIndex: -1,
        title: "",
        stepIndex: 0,
        checks: [],
        status: "pending",
        requiresProctoring: true,
      }

      if (section.selfSectionIndex === -1) return
      let check: SystemCheckStep = section.checks[action.payload.stepIndex] || {
        title: "",
        description: "",
        status: "pending",
        checkComponentName: "PauseCheck",
        selfSectionIndex: -1,
        selfStepIndex: -1,
        full: false,
      }

      if (check.selfStepIndex === -1) return
      check.status = action.payload.status

      let statusCounts: {
        [key in SystemCheckStatus]: number
      } = {
        pending: 0,
        success: 0,
        active: 0,
        error: 0,
      }

      for (check of section.checks) {
        statusCounts[check.status] += 1
      }

      if (statusCounts.error > 0) {
        section.status = "error"
        return
      }

      if (statusCounts.pending > 0) {
        section.status = "pending"
        return
      }

      if (statusCounts.active > 0) {
        section.status = "active"
        return
      }

      section.status = "success"
    },
    addSystemCheckData: (
      state,
      action: PayloadAction<{
        key: string
        value: any
      }>
    ) => {
      state.data[action.payload.key] = action.payload.value
    },
    setAutomatedFlow: (state, action: PayloadAction<boolean>) => {
      state.automatedFlow = action.payload
    },
    setIsAutomatedFlowModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isAutomatedFlowModalOpen = action.payload
    },
    resetSystemCheck: (state) => {
      state.sections = initialState.sections
      state.sectionIndex = initialState.sectionIndex
      state.data = initialState.data
    },
    updateSectionsForInterviewMode: (
      state,
      action: PayloadAction<{
        interviewMode: string
        dualCameraRequired: boolean | undefined
      }>
    ) => {
      if (action.payload.interviewMode === INTERVIEW_MODE.non_proctored) {
        state.sections = state.sections.filter((section) => !section.requiresProctoring)
      } else if (!action.payload.dualCameraRequired) {
        state.sections = state.sections.filter((section) => section.id !== SystemCheckSectionIds.mobile_camera_pairing)
      }

      if (isTabletAndBelow()) {
        state.sections = state.sections.filter(
          (section) =>
            section.id !== SystemCheckSectionIds.full_screen_mode &&
            section.id !== SystemCheckSectionIds.screen_share_permission
        )
      }

      state.sections = state.sections.map((section, index) => ({
        ...section,
        selfSectionIndex: index,
        checks: section.checks.map((check) => ({
          ...check,
          selfSectionIndex: index,
        })),
      }))
    },
  },
})

export const {
  setSystemCheckData,
  setSystemCheckStepIndex,
  setSystemCheckStepStatus,
  addSystemCheckData,
  setAutomatedFlow,
  setIsAutomatedFlowModalOpen,
  resetSystemCheck,
  updateSectionsForInterviewMode,
} = systemCheckSlice.actions
export default systemCheckSlice.reducer
